
import { useState } from 'react';
import { transitRoutes, parkingLocations, transportationTips, fares } from '../../../mocks/transportation';

export default function TransportationGuide() {
  const [activeTab, setActiveTab] = useState<'transit' | 'parking'>('transit');
  const [selectedRoute, setSelectedRoute] = useState<number | null>(null);

  return (
    <section id="transportation" className="py-20 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold px-4 py-2 rounded-full mb-4 uppercase tracking-wide">
            Getting Around
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Transportation Guide
          </h2>
          <p className="text-gray-600 text-base max-w-3xl mx-auto">
            Navigate Roseville with ease using public transit, find convenient parking, and discover the best ways to explore our city
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-100 p-1 rounded-full inline-flex">
            <button
              onClick={() => setActiveTab('transit')}
              className={`px-8 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center space-x-2 ${
                activeTab === 'transit'
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-bus-2-line text-lg"></i>
              <span>Transit Routes</span>
            </button>
            <button
              onClick={() => setActiveTab('parking')}
              className={`px-8 py-3 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center space-x-2 ${
                activeTab === 'parking'
                  ? 'bg-teal-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-parking-line text-lg"></i>
              <span>Parking Info</span>
            </button>
          </div>
        </div>

        {/* Transit Routes Tab */}
        {activeTab === 'transit' && (
          <div className="space-y-12">
            {/* Quick Tips */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {transportationTips.map((tip, index) => (
                <div key={index} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-3">
                    <i className={`${tip.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h4 className="font-semibold text-gray-900 text-sm mb-1">{tip.title}</h4>
                  <p className="text-gray-500 text-xs">{tip.description}</p>
                </div>
              ))}
            </div>

            {/* Routes Grid */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <i className="ri-route-line text-teal-600"></i>
                <span>Roseville Transit Routes</span>
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {transitRoutes.map((route) => (
                  <div
                    key={route.id}
                    onClick={() => setSelectedRoute(selectedRoute === route.id ? null : route.id)}
                    className={`bg-white rounded-xl p-5 shadow-sm border-2 transition-all cursor-pointer ${
                      selectedRoute === route.id
                        ? 'border-teal-500 shadow-lg'
                        : 'border-gray-100 hover:border-teal-200 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: route.color }}
                        >
                          {route.name.split(' ')[1]}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm">{route.name}</h4>
                          <span className="text-xs text-gray-500">{route.type}</span>
                        </div>
                      </div>
                      <i className={`ri-arrow-${selectedRoute === route.id ? 'up' : 'down'}-s-line text-gray-400`}></i>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{route.description}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="flex items-center space-x-1">
                        <i className="ri-time-line"></i>
                        <span>{route.frequency}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <i className="ri-calendar-line"></i>
                        <span>{route.hours}</span>
                      </span>
                    </div>
                    
                    {/* Expanded Stops */}
                    {selectedRoute === route.id && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="text-xs font-semibold text-gray-700 mb-2">Route Stops:</p>
                        <div className="flex flex-wrap gap-2">
                          {route.stops.map((stop, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                            >
                              <span className="w-2 h-2 rounded-full mr-2" style={{ backgroundColor: route.color }}></span>
                              {stop}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Fares Section */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-8 text-white">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4 flex items-center space-x-2">
                    <i className="ri-ticket-2-line"></i>
                    <span>Transit Fares</span>
                  </h3>
                  <p className="text-teal-100 mb-6">
                    Affordable fares for everyone. Purchase passes at the Transit Center or through the Roseville Transit app.
                  </p>
                  <a
                    href="https://www.roseville.ca.us/government/departments/public_works/roseville_transit"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-white text-teal-700 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span>Plan Your Trip</span>
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {fares.map((fare, index) => (
                    <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                      <p className="text-teal-100 text-xs mb-1">{fare.type}</p>
                      <p className="text-2xl font-bold">{fare.price}</p>
                      <p className="text-teal-200 text-xs">{fare.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Parking Tab */}
        {activeTab === 'parking' && (
          <div className="space-y-12">
            {/* Parking Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-full mx-auto mb-3">
                  <i className="ri-parking-box-line text-2xl text-teal-600"></i>
                </div>
                <p className="text-3xl font-bold text-gray-900">7,400+</p>
                <p className="text-gray-500 text-sm">Public Spaces</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-3">
                  <i className="ri-money-dollar-circle-line text-2xl text-green-600"></i>
                </div>
                <p className="text-3xl font-bold text-gray-900">Free</p>
                <p className="text-gray-500 text-sm">Most Locations</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-3">
                  <i className="ri-charging-pile-2-line text-2xl text-amber-600"></i>
                </div>
                <p className="text-3xl font-bold text-gray-900">50+</p>
                <p className="text-gray-500 text-sm">EV Chargers</p>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-indigo-50 rounded-full mx-auto mb-3">
                  <i className="ri-wheelchair-line text-2xl text-indigo-600"></i>
                </div>
                <p className="text-3xl font-bold text-gray-900">ADA</p>
                <p className="text-gray-500 text-sm">Accessible</p>
              </div>
            </div>

            {/* Parking Locations Grid */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <i className="ri-map-pin-line text-teal-600"></i>
                <span>Parking Locations</span>
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {parkingLocations.map((location) => (
                  <div
                    key={location.id}
                    className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-lg hover:border-teal-200 transition-all group"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-teal-50 rounded-xl group-hover:bg-teal-100 transition-colors">
                        <i className={`${location.icon} text-2xl text-teal-600`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">
                            {location.name}
                          </h4>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                            {location.type}
                          </span>
                        </div>
                        <p className="text-gray-500 text-sm mb-3 flex items-center space-x-1">
                          <i className="ri-map-pin-2-line"></i>
                          <span>{location.address}</span>
                        </p>
                        <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                          <div className="flex items-center space-x-1 text-gray-600">
                            <i className="ri-car-line"></i>
                            <span>{location.spaces} spaces</span>
                          </div>
                          <div className="flex items-center space-x-1 text-gray-600">
                            <i className="ri-time-line"></i>
                            <span>{location.hours}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-teal-600 font-semibold text-sm">{location.rate}</span>
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address + ', Roseville, CA')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-gray-500 hover:text-teal-600 flex items-center space-x-1 cursor-pointer"
                          >
                            <i className="ri-direction-line"></i>
                            <span>Directions</span>
                          </a>
                        </div>
                        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-1">
                          {location.features.map((feature, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded-full"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Parking Tips */}
            <div className="bg-amber-50 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                <i className="ri-lightbulb-line text-amber-600"></i>
                <span>Parking Tips</span>
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                    <span className="text-amber-800 font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Downtown Events</h4>
                    <p className="text-gray-600 text-sm">Use the Vernon Street Garage for free 2-hour parking during Downtown Tuesday Nights</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                    <span className="text-amber-800 font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">Galleria Shopping</h4>
                    <p className="text-gray-600 text-sm">Park on upper levels for easier access and shorter walks to stores</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-amber-200 rounded-full flex-shrink-0">
                    <span className="text-amber-800 font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm mb-1">EV Charging</h4>
                    <p className="text-gray-600 text-sm">ChargePoint stations available at Galleria, Civic Center, and major shopping centers</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Need help planning your trip?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:916-745-7560"
              className="inline-flex items-center space-x-2 bg-teal-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              <span>Call Transit: (916) 745-7560</span>
            </a>
            <a
              href="https://www.roseville.ca.us/government/departments/public_works/roseville_transit"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-white text-teal-600 border-2 border-teal-600 px-6 py-3 rounded-full font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              <span>Visit Transit Website</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
